import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Ticket, TicketComment } from "../../data/ticket"
import apiClient from "../../api/apiClient"
import { serializeFirestoreData } from "../../utils/timestamp"

type StatusCounts = { open: number; closed: number }
export interface TicketCounts {
  total: StatusCounts
  byBuilding: Record<string, StatusCounts>
}

interface TicketsState {
  tickets: Ticket[]
  ticketCounts: TicketCounts // Open / closed counts for the admin filter buttons and building dropdown
  currentTicket: Ticket | null
  isLoading: boolean
  error: string | null
}

const initialState: TicketsState = {
  tickets: [],
  ticketCounts: { total: { open: 0, closed: 0 }, byBuilding: {} },
  currentTicket: null,
  isLoading: false,
  error: null,
}

export const fetchTickets = createAsyncThunk(
  "tickets/fetchTickets",
  async (
    { userId, status, signal }: { userId: string; status?: string; signal?: AbortSignal },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.get(`/tickets${status ? `?status=${status}` : ""}`, { signal })
      return serializeFirestoreData(response.data.tickets)
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled")
      }
      return rejectWithValue(error.response?.data?.error || "Failed to fetch tickets")
    }
  },
)

export const fetchAllTickets = createAsyncThunk(
  "tickets/fetchAllTickets",
  async (
    { status, building, signal }: { status?: string; building?: string; signal?: AbortSignal },
    { rejectWithValue },
  ) => {
    try {
      let url = "/tickets/admin/all"
      const params = new URLSearchParams()
      if (status) params.append("status", status)
      if (building) params.append("building", building)

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await apiClient.get(url, { signal })
      return serializeFirestoreData(response.data.tickets)
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled")
      }
      return rejectWithValue(error.response?.data?.error || "Failed to fetch tickets")
    }
  },
)

export const createTicket = createAsyncThunk(
  "tickets/createTicket",
  async (
    {
      type,
      title,
      description,
      image,
    }: {
      type: string
      title: string
      description: string
      image?: File | null
    },
    { rejectWithValue },
  ) => {
    try {
      // Create FormData if there's an image
      let data
      if (image) {
        data = new FormData()
        data.append("type", type)
        data.append("title", title)
        data.append("description", description)
        data.append("image", image)
      } else {
        data = {
          type,
          title,
          description,
        }
      }

      const response = await apiClient.post("/tickets", data, {
        headers: {
          ...(image ? { "Content-Type": "multipart/form-data" } : { "Content-Type": "application/json" }),
        },
      })

      return { success: true, ticket: serializeFirestoreData(response.data.ticket) }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to create ticket")
    }
  },
)

export const getTicketById = createAsyncThunk(
  "tickets/getTicketById",
  async (ticketId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/tickets/${ticketId}`)
      return serializeFirestoreData(response.data.ticket)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to get ticket")
    }
  },
)

export const addTicketComment = createAsyncThunk(
  "tickets/addTicketComment",
  async (
    {
      ticketId,
      content,
      attachment,
    }: {
      ticketId: string
      content: string
      attachment?: File | null
    },
    { rejectWithValue },
  ) => {
    try {
      let data
      let headers = {}

      if (attachment) {
        // Create FormData for file upload
        data = new FormData()
        data.append("content", content)
        data.append("attachment", attachment)
        data.append("attachmentName", attachment.name)
        data.append("attachmentType", attachment.type)
        headers = { "Content-Type": "multipart/form-data" }
      } else {
        // Regular JSON request
        data = { content }
        headers = { "Content-Type": "application/json" }
      }

      const response = await apiClient.post(`/tickets/${ticketId}/comment`, data, { headers })

      return {
        ticketId,
        comment: serializeFirestoreData(response.data.comment),
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to add comment")
    }
  },
)

export const updateTicketStatus = createAsyncThunk(
  "tickets/updateTicketStatus",
  async ({ ticketId, status }: { ticketId: string; status: "open" | "closed" }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/tickets/${ticketId}/status`, { status })
      return { success: true, ticketId, status }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update ticket status")
    }
  },
)

export const deleteTicket = createAsyncThunk("tickets/deleteTicket", async (ticketId: string, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/tickets/${ticketId}`)
    return { success: true, ticketId }
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.error || "Failed to delete ticket")
  }
})

// Counts only — loading every ticket just to count them was most of the project's Firestore reads
export const fetchTicketCounts = createAsyncThunk(
  "tickets/fetchTicketCounts",
  async ({ signal }: { signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/tickets/admin/counts", { signal })
      return response.data as TicketCounts
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "CanceledError") {
        return rejectWithValue("Request cancelled")
      }
      return rejectWithValue(error.response?.data?.error || "Failed to fetch ticket counts")
    }
  },
)

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    clearCurrentTicket: (state) => {
      state.currentTicket = null
    },
    clearTickets: (state) => {
      state.tickets = []
    },
    clearTicketCounts: (state) => {
      state.ticketCounts = initialState.ticketCounts
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTickets.fulfilled, (state, action: PayloadAction<Ticket[]>) => {
        state.tickets = action.payload
        state.isLoading = false
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload !== "Request cancelled") {
          state.error = action.payload as string
        }
      })
      .addCase(fetchAllTickets.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAllTickets.fulfilled, (state, action: PayloadAction<Ticket[]>) => {
        state.tickets = action.payload
        state.isLoading = false
      })
      .addCase(fetchAllTickets.rejected, (state, action) => {
        state.isLoading = false
        if (action.payload !== "Request cancelled") {
          state.error = action.payload as string
        }
      })
      .addCase(createTicket.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        if (action.payload.ticket) {
          state.tickets.unshift(action.payload.ticket as Ticket)
        }
        state.isLoading = false
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(getTicketById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getTicketById.fulfilled, (state, action: PayloadAction<Ticket>) => {
        state.currentTicket = action.payload
        state.isLoading = false
      })
      .addCase(getTicketById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(addTicketComment.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addTicketComment.fulfilled, (state, action) => {
        const { ticketId, comment } = action.payload

        // Update the current ticket if it's the one being commented on
        if (state.currentTicket && state.currentTicket.id === ticketId) {
          state.currentTicket.comments.push(comment as TicketComment)
        }

        // Also update in the tickets list
        const ticket = state.tickets.find((t) => t.id === ticketId)
        if (ticket) {
          ticket.comments.push(comment as TicketComment)
        }

        state.isLoading = false
      })
      .addCase(addTicketComment.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(updateTicketStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        const { ticketId, status } = action.payload

        // Update the current ticket if it's the one being updated
        if (state.currentTicket && state.currentTicket.id === ticketId) {
          state.currentTicket.status = status
        }

        // Also update in the tickets list
        const ticket = state.tickets.find((t) => t.id === ticketId)
        if (ticket) {
          ticket.status = status
        }

        state.isLoading = false
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(deleteTicket.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteTicket.fulfilled, (state, action) => {
        const { ticketId } = action.payload

        // Remove the ticket from the tickets list
        state.tickets = state.tickets.filter((ticket) => ticket.id !== ticketId)

        // Clear current ticket if it's the one being deleted
        if (state.currentTicket && state.currentTicket.id === ticketId) {
          state.currentTicket = null
        }

        state.isLoading = false
      })
      .addCase(deleteTicket.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchTicketCounts.pending, (state) => {
        state.error = null
      })
      .addCase(fetchTicketCounts.fulfilled, (state, action: PayloadAction<TicketCounts>) => {
        state.ticketCounts = action.payload
      })
      .addCase(fetchTicketCounts.rejected, (state, action) => {
        if (action.payload !== "Request cancelled") {
          state.error = action.payload as string
        }
      })
  },
})

export const { clearCurrentTicket, clearTickets, clearTicketCounts } = ticketsSlice.actions
export default ticketsSlice.reducer
